import React, { createContext, useContext, useReducer, useMemo } from 'react';

const AppContext = createContext();

const initialState = {
  currentView: 'home',
  currentSlide: 1,
  boardingPass: null,
  userInfo: {
    firstName: 'Passenger',
    selectedIcon: null
  },
  questionAnswers: {
    q1: null,
    q2: null,
    q3: null
  },
  gameProgress: {
    game1Completed: false,
    game2Completed: false,
    game3Completed: false,
    completedGames: []
  },
  boardingPassGenerated: false,
  boardingPassDownloaded: false,
  contentLoaded: false,
  slideContent: {}
};

function appReducer(state, action) {
  switch (action.type) {
    case 'SET_VIEW':
      return {
        ...state,
        currentView: action.payload
      };

    case 'SET_SLIDE':
      return {
        ...state,
        currentSlide: action.payload
      };

    case 'UPDATE_USER_INFO':
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          ...action.payload
        }
      };

    case 'UPDATE_QUESTION_ANSWER':
      return {
        ...state,
        questionAnswers: {
          ...state.questionAnswers,
          [action.payload.questionId]: action.payload.answer
        }
      };

    case 'SET_BOARDING_PASS_DOWNLOADED':
      return {
        ...state,
        boardingPassDownloaded: action.payload
      };

    case 'COMPLETE_GAME':
      const gameId = action.payload;
      return {
        ...state,
        gameProgress: {
          ...state.gameProgress,
          [`game${gameId}Completed`]: true,
          completedGames: [...state.gameProgress.completedGames, gameId]
        }
      };

    case 'RESET_GAME':
      const resetGameId = action.payload;
      return {
        ...state,
        gameProgress: {
          ...state.gameProgress,
          [`game${resetGameId}Completed`]: false,
          completedGames: state.gameProgress.completedGames.filter(
            (id) => id !== resetGameId
          )
        }
      };

    case 'GENERATE_BOARDING_PASS':
      return {
        ...state,
        boardingPassGenerated: true
      };

    case 'SET_BOARDING_PASS':
      return {
        ...state,
        boardingPass: action.payload,
        boardingPassGenerated: true
      };

    case 'SET_CONTENT_LOADED':
      return {
        ...state,
        contentLoaded: action.payload
      };

    case 'SET_SLIDE_CONTENT':
      return {
        ...state,
        slideContent: {
          ...state.slideContent,
          ...action.payload
        }
      };

    case 'RESET_EXPERIENCE':
      return {
        ...initialState,
        contentLoaded: state.contentLoaded,
        slideContent: state.slideContent
      };

    default:
      return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  const actions = useMemo(
    () => ({
      setView: (view) =>
        dispatch({
          type: 'SET_VIEW',
          payload: view
        }),
      setSlide: (slide) =>
        dispatch({
          type: 'SET_SLIDE',
          payload: slide
        }),
      updateUserInfo: (info) =>
        dispatch({
          type: 'UPDATE_USER_INFO',
          payload: info
        }),
      setSelectedIcon: (icon) =>
        dispatch({
          type: 'UPDATE_USER_INFO',
          payload: {
            selectedIcon: icon
          }
        }),
      completeGame: (gameId) =>
        dispatch({
          type: 'COMPLETE_GAME',
          payload: gameId
        }),
      updateQuestionAnswer: (questionId, answer) =>
        dispatch({
          type: 'UPDATE_QUESTION_ANSWER',
          payload: {
            questionId,
            answer
          }
        }),
      setBoardingPassDownloaded: (downloaded) =>
        dispatch({
          type: 'SET_BOARDING_PASS_DOWNLOADED',
          payload: downloaded
        }),
      resetGame: (gameId) =>
        dispatch({
          type: 'RESET_GAME',
          payload: gameId
        }),
      generateBoardingPass: () =>
        dispatch({
          type: 'GENERATE_BOARDING_PASS'
        }),
      setBoardingPass: (boardingPassData) =>
        dispatch({
          type: 'SET_BOARDING_PASS',
          payload: boardingPassData
        }),
      setContentLoaded: (loaded) =>
        dispatch({
          type: 'SET_CONTENT_LOADED',
          payload: loaded
        }),
      setSlideContent: (content) =>
        dispatch({
          type: 'SET_SLIDE_CONTENT',
          payload: content
        }),
      resetExperience: () =>
        dispatch({
          type: 'RESET_EXPERIENCE'
        })
    }),
    [dispatch]
  );

  return (
    <AppContext.Provider
      value={{
        state,
        actions
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
